import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  MeterOnboardingStateProps,
  meterOnboardingState,
} from '../../atoms/meterOnboardingWizardAtom';
import { UIStateProps, uiState } from '../../atoms/uiAtom';
import { useGetConventionAvailability, usePostValidateEan } from '../../hooks/meters';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import Button from '../common/Button';
import { EanEligibilityStatusEnum } from '../../enum/EanEligibilityStatusEnum';
import { EanUtils } from '../../common/eanUtils';
import Input from '../forms/Input';
import Loader from '../common/Loader';
import ModalMeterConsentDecline from '../modals/ModalMeterConsentDecline';
import ModalMeterConsentRefused from '../modals/ModalMeterConsentRefused';
import { ModalResponse } from '../../enum/ModalResponse';
import { SDPReadingConsentEnum } from '../../enum/SDPReadingConsentEnum';
import { SDPReadingNicheEnum } from '../../enum/SDPReadingNicheEnum';
import Skeleton from 'react-loading-skeleton';
import classNames from 'classnames';
import { routes } from '../../routes';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useWizard } from 'react-use-wizard';
import { userPreferencesState } from '../../atoms/userPreferencesAtom/userPreferencesAtom';

export const STEP_CONSENT_METER_ID = 'step-consent-meter';
export default function StepConsentMeter() {
  const { t } = useTranslation();

  const { register, handleSubmit, setValue } = useForm({
    defaultValues: {
      consent: false,
    },
  });

  const { nextStep } = useWizard();

  const [isOpenModalRefusedMeterConsent, setIsOpenModalRefusedMeterConsent] = useState(false);
  const [isOpenModalDeclineMeterConsent, setIsOpenModalDeclineMeterConsent] = useState(false);

  const setUiState = useSetRecoilState<UIStateProps>(uiState);
  const meterOnboarding = useRecoilValue<MeterOnboardingStateProps>(meterOnboardingState);

  const userPreferences = useRecoilValue(userPreferencesState);
  const {
    isFetching: isFetchingConventionAvailability,
    isError: isFetchConventionAvailabilityError,
    data: readingConsentAvailability,
    refetch: fetchConventionAvailability,
  } = useGetConventionAvailability(meterOnboarding.ean);

  const [readingConsent, setReadingConsent] = useState<string | undefined>(
    SDPReadingConsentEnum.NO
  );
  const { isPending: isPendingPostValidateEan, mutateAsync: postValidateEan } =
    usePostValidateEan();

  const onSubmit = async (data: any) => {
    if (
      data.consent === false &&
      meterOnboarding.eanEligibilityOutResponse.isSmart === true &&
      meterOnboarding.eanEligibilityOutResponse.readingNiche === SDPReadingNicheEnum.OPTIONAL
    ) {
      setIsOpenModalDeclineMeterConsent(true);
    } else {
      addMeter(data.consent);
    }
  };

  const renderConsentTitle = () => {
    return (
      <div className="step-consent-meter__header">
        <h2 className="step-consent-meter__header__title">{t('step.consent.meter.title')}</h2>
        <h3
          className={classNames('step-consent-meter__header__subtitle', {
            'step-consent-meter__header__subtitle--disabled': hasConsentCheckboxDisabled(),
          })}
        >
          {t('step.consent.meter.subtitle')}
        </h3>
      </div>
    );
  };

  useEffect(() => {
    const renderWarningMessage = () => {
      let message;
      if (meterOnboarding.isAccMember === true) {
        message = t('step.consent.meter.warning.acc.member');
      } else if (meterOnboarding.eanEligibilityOutResponse.isSmart === true) {
        if (
          meterOnboarding.eanEligibilityOutResponse.readingNiche === SDPReadingNicheEnum.MANDATORY
        ) {
          message = t('step.consent.meter.warning.niche.mandatory');
        } else if (meterOnboarding.eanEligibilityOutResponse.isCapable === false) {
          message = t('step.consent.meter.warning.not.capable');
        }
      } else if (
        meterOnboarding.eanEligibilityOutResponse.isSmart === false &&
        meterOnboarding.eanEligibilityOutResponse.readingNiche !== SDPReadingNicheEnum.MANDATORY &&
        meterOnboarding.eanEligibilityOutResponse.readingNiche !== SDPReadingNicheEnum.OPTIONAL
      ) {
        message = t('step.consent.meter.warning.classic');
      }
      return message;
    };
    const warningMessage = renderWarningMessage();
    if (warningMessage) {
      setUiState((prev) => ({
        ...prev,
        hasWarning: warningMessage ? { message: warningMessage, route: routes.METERS.path } : null,
      }));
    }
    if (userPreferences.selectedSdpId) {
      fetchConventionAvailability().then((conventionAvailability) => {
        if (conventionAvailability.error) {
          setUiState((prev) => ({ ...prev, hasError: true }));
        } else {
          setReadingConsent(meterOnboarding.eanEligibilityOutResponse.readingConsent);
        }
      });
    } else {
      setReadingConsent(meterOnboarding.eanEligibilityOutResponse.readingConsent);
    }
  }, [
    fetchConventionAvailability,
    meterOnboarding.eanEligibilityOutResponse.isCapable,
    meterOnboarding.eanEligibilityOutResponse.isSmart,
    meterOnboarding.eanEligibilityOutResponse.readingConsent,
    meterOnboarding.eanEligibilityOutResponse.readingNiche,
    userPreferences.selectedSdpId,
    setUiState,
    t,
    meterOnboarding.isAccMember,
  ]);
  const hasConsentCheckboxDisabled = useCallback(() => {
    if (readingConsentAvailability?.data.readingConsent === SDPReadingConsentEnum.NO) {
      return true;
    } else {
      if (
        meterOnboarding.eanEligibilityOutResponse.readingNiche === SDPReadingNicheEnum.MANDATORY ||
        meterOnboarding.isAccMember === true
      ) {
        return true;
      } else if (
        meterOnboarding.eanEligibilityOutResponse.readingNiche === SDPReadingNicheEnum.OPTIONAL
      ) {
        return false;
      }
    }
    return false;
  }, [
    meterOnboarding.eanEligibilityOutResponse.readingNiche,
    meterOnboarding.isAccMember,
    readingConsentAvailability?.data.readingConsent,
  ]);

  const getConsentCheckboxValue = useCallback(() => {
    if (hasConsentCheckboxDisabled() === true) {
      if (
        readingConsent === SDPReadingConsentEnum.YES ||
        meterOnboarding.eanEligibilityOutResponse.readingNiche === SDPReadingNicheEnum.MANDATORY
      ) {
        return true;
      } else {
        return false;
      }
    }
    if (
      meterOnboarding.eanEligibilityOutResponse.readingNiche === SDPReadingNicheEnum.MANDATORY ||
      meterOnboarding.isAccMember === true
    ) {
      return true;
    } else if (
      meterOnboarding.eanEligibilityOutResponse.readingNiche === SDPReadingNicheEnum.OPTIONAL
    ) {
      if (readingConsent === SDPReadingConsentEnum.YES) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }, [
    hasConsentCheckboxDisabled,
    readingConsent,
    meterOnboarding.eanEligibilityOutResponse.readingNiche,
    meterOnboarding.isAccMember,
  ]);

  useEffect(() => {
    setValue('consent', getConsentCheckboxValue());
  }, [getConsentCheckboxValue, setValue]);

  const handlePreviousStep = () => {
    setIsOpenModalRefusedMeterConsent(true);
  };

  const handleClickActionModalRefusedMeterConsent = () => {
    setIsOpenModalRefusedMeterConsent(false);
  };

  const addMeter = (consent: boolean) => {
    postValidateEan({
      ean: meterOnboarding.ean,
      rate: meterOnboarding.tariff,
      name: meterOnboarding.name,
      consent,
      eligibility: meterOnboarding.eanEligibilityOutResponse,
    })
      .then((responseValidateEan) => {
        if (EanUtils.isEligibilityError(responseValidateEan.status)) {
          setUiState((prev) => ({
            ...prev,
            hasError: t(EanUtils.getEanEligibilityStatusError(EanEligibilityStatusEnum.Conflict)),
          }));
        } else {
          const eanEligibilityStatus = EanUtils.getEanEligibilityStatus(
            responseValidateEan.data!.code
          );
          if (eanEligibilityStatus === EanEligibilityStatusEnum.Success) {
            nextStep();
          } else {
            setUiState((prev) => ({
              ...prev,
              hasError: t(EanUtils.getEanEligibilityStatusError(eanEligibilityStatus)),
            }));
          }
        }
      })
      .catch(() => {
        setUiState((prev) => ({
          ...prev,
          hasError: true,
        }));
      });
  };

  const handleClickActionModalDeclineMeterConsent = () => {
    setIsOpenModalDeclineMeterConsent(false);
  };

  const handleClickCloseModalDeclineMeterConsent = (response: ModalResponse | undefined) => {
    if (response === ModalResponse.CANCEL) {
      addMeter(false);
    }
  };

  return (
    <div className="wizard step-consent-meter">
      {renderConsentTitle()}
      <form className="form" onSubmit={handleSubmit(onSubmit)}>
        <div className="form__container">
          {isFetchingConventionAvailability || isFetchConventionAvailabilityError ? (
            <Fragment>
              <Skeleton count={1} width="50%" />
              <Skeleton count={1} width="70%" />
            </Fragment>
          ) : (
            meterOnboarding.eanEligibilityOutResponse.isSmart && (
              <Input
                type="checkbox"
                name="consent"
                register={register}
                isDisabled={hasConsentCheckboxDisabled()}
                suffix={
                  <span>
                    <h3>{t('step.consent.meter.consent.input.label')}</h3>
                    <p>{t('step.consent.meter.consent.input.caption')}</p>
                  </span>
                }
              />
            )
          )}
        </div>

        <div className="wizard__actions">
          <Button className="btn btn--secondary" onClick={handlePreviousStep}>
            {t('common.refused')}
          </Button>
          <Button
            isDisabled={isPendingPostValidateEan}
            prefix={isPendingPostValidateEan ? <Loader /> : null}
            type="submit"
            className="btn btn--primary"
          >
            {t('common.accepted')}
          </Button>
        </div>
      </form>
      <ModalMeterConsentRefused
        isOpen={isOpenModalRefusedMeterConsent}
        onClickAction={handleClickActionModalRefusedMeterConsent}
      />
      <ModalMeterConsentDecline
        isOpen={isOpenModalDeclineMeterConsent}
        onClickAction={handleClickActionModalDeclineMeterConsent}
        onClose={handleClickCloseModalDeclineMeterConsent}
      />
    </div>
  );
}
